class SlideshowScrollbar extends HTMLElement {
  connectedCallback() {
    this.slideshow = this.closest('slideshow-component');
    this.container = this.querySelector('[ref="scrollbarContainer"]');
    this.thumb = this.querySelector('[ref="scrollbarThumb"]');

    if (!this.slideshow || !this.container || !this.thumb) return;

    // Bind handlers once and store references for proper cleanup
    this._handleResize = this.handleResize.bind(this);
    this._handleTrackClick = this.handleTrackClick.bind(this);
    this._handleDragStart = this.handleDragStart.bind(this);
    this._handleSlideshowSelect = this.updateThumbPosition.bind(this);

    // Track initialization state
    this._initialized = false;
    this._listenersAttached = false;

    // Wait for slideshow to be ready and get slide count
    this.initializeScrollbar();

    // Use ResizeObserver to detect when element becomes visible (e.g., tab switch)
    this._setupVisibilityObserver();
  }

  disconnectedCallback() {
    this._cleanup();
  }

  _cleanup() {
    // Clean up event listeners properly using stored references
    if (this._listenersAttached) {
      if (this.slideshow) {
        this.slideshow.removeEventListener('slideshow:select', this._handleSlideshowSelect);
      }
      if (this.container) {
        this.container.removeEventListener('click', this._handleTrackClick);
        this.container.removeEventListener('pointerdown', this._handleDragStart);
      }
      window.removeEventListener('resize', this._handleResize);
      this._listenersAttached = false;
    }

    // Disconnect observers
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  _setupVisibilityObserver() {
    // Use ResizeObserver to detect size changes (including when element becomes visible)
    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        // If width changed from 0 to something, reinitialize
        if (width > 0 && (!this.containerWidth || this.containerWidth === 0)) {
          this.reinit();
        } else if (width > 0 && width !== this.containerWidth) {
          // Width changed, update dimensions
          this.updateThumbDimensions();
          this.updateThumbPosition();
        }
      }
    });

    this._resizeObserver.observe(this.container);
  }

  initializeScrollbar() {
    // Use a small delay to ensure slideshow is fully initialized
    setTimeout(() => {
      this._doInit();
    }, 10);
  }

  _doInit() {
    if (!this.slideshow || !this.container || !this.thumb) return;

    const slides = this.slideshow.querySelectorAll('[ref="slides[]"]:not(.product-media-container)');
    this.slideCount = slides.length;

    // Get column settings from data attributes
    const desktopColumns = parseInt(this.getAttribute('data-columns') || '1');
    const mobileColumns = parseInt(this.getAttribute('data-columns-mobile') || '1');

    // Determine current columns based on screen size
    const isMobile = window.innerWidth < 750; // Shopify's mobile breakpoint
    this.visibleColumns = isMobile ? mobileColumns : desktopColumns;

    if (this.slideCount > 0) {
      // Calculate dimensions
      this.updateThumbDimensions();

      // Attach listeners only once
      if (!this._listenersAttached) {
        this.slideshow.addEventListener('slideshow:select', this._handleSlideshowSelect);
        this.container.addEventListener('click', this._handleTrackClick);
        this.container.addEventListener('pointerdown', this._handleDragStart);
        window.addEventListener('resize', this._handleResize);
        this._listenersAttached = true;
      }

      // Initial position
      this.updateThumbPosition();
      this._initialized = true;
    }
  }

  /**
   * Public method to reinitialize the scrollbar.
   * Call this when the scrollbar becomes visible (e.g., after tab switch).
   */
  reinit() {
    // Re-query slides in case DOM changed
    const slides = this.slideshow?.querySelectorAll('[ref="slides[]"]:not(.product-media-container)');
    if (slides) {
      this.slideCount = slides.length;
    }

    // Update column settings
    const desktopColumns = parseInt(this.getAttribute('data-columns') || '1');
    const mobileColumns = parseInt(this.getAttribute('data-columns-mobile') || '1');
    const isMobile = window.innerWidth < 750;
    this.visibleColumns = isMobile ? mobileColumns : desktopColumns;

    // Recalculate dimensions and position
    this.updateThumbDimensions();
    this.updateThumbPosition();
  }

  updateThumbDimensions() {
    if (!this.container || !this.thumb) return;

    // Get container width in pixels
    const containerRect = this.container.getBoundingClientRect();
    this.containerWidth = containerRect.width;

    // Skip if container is not visible (width = 0)
    if (!this.containerWidth) {
      return;
    }

    // Calculate thumb width in pixels based on visible columns vs total slides
    const thumbWidthRatio = Math.min(1, this.visibleColumns / this.slideCount);
    this.thumbWidth = Math.max(20, this.containerWidth * thumbWidthRatio); // Minimum 20px width

    // Set CSS variables
    this.thumb.style.setProperty('--thumb-width-px', `${this.thumbWidth}px`);

    // Calculate available travel distance
    this.maxThumbPosition = this.containerWidth - this.thumbWidth;
  }

  updateThumbPosition() {
    if (!this.slideshow || !this.thumb || this.slideCount === 0) return;

    // If dimensions not calculated yet, try to calculate first
    if (!this.containerWidth || this.maxThumbPosition == null) {
      this.updateThumbDimensions();
      if (!this.containerWidth) return;
    }

    const currentSlide = this.slideshow.current || 0;

    // Calculate the maximum scroll position
    const maxScrollPosition = Math.max(0, this.slideCount - this.visibleColumns);

    // Clamp current slide to not exceed max scroll position
    const clampedCurrentSlide = Math.min(currentSlide, maxScrollPosition);

    // Calculate progress (0 to 1)
    const progress = maxScrollPosition > 0 ? clampedCurrentSlide / maxScrollPosition : 0;

    // Calculate thumb position in pixels
    const thumbPositionPx = progress * this.maxThumbPosition;

    this.thumb.style.setProperty('--thumb-position-px', `${thumbPositionPx}px`);
  }

  handleResize() {
    // Update visible columns on resize
    const desktopColumns = parseInt(this.getAttribute('data-columns') || '1');
    const mobileColumns = parseInt(this.getAttribute('data-columns-mobile') || '1');
    const isMobile = window.innerWidth < 750;

    const newVisibleColumns = isMobile ? mobileColumns : desktopColumns;

    if (newVisibleColumns !== this.visibleColumns) {
      this.visibleColumns = newVisibleColumns;
    }

    // Always recalculate dimensions on resize since container width may have changed
    this.updateThumbDimensions();
    this.updateThumbPosition();
  }

  handleTrackClick(event) {
    if (event.target === this.thumb) return; // Don't handle clicks on the thumb itself

    const rect = this.container.getBoundingClientRect();
    const clickX = event.clientX - rect.left;

    // Calculate progress based on pixel position
    const progress = this.maxThumbPosition > 0 ? clickX / this.containerWidth : 0;

    // Calculate target slide based on visible columns
    const maxScrollPosition = Math.max(0, this.slideCount - this.visibleColumns);
    const targetSlide = Math.min(Math.round(progress * maxScrollPosition), maxScrollPosition);

    this.slideshow.select(targetSlide);
  }

  handleDragStart(event) {
    if (!event.target.closest('[ref="scrollbarThumb"]')) return;

    event.preventDefault();
    this.isDragging = true;

    const handleDrag = (e) => {
      if (!this.isDragging) return;

      const rect = this.container.getBoundingClientRect();
      const dragX = e.clientX - rect.left;

      // Calculate progress based on pixel position
      const progress = Math.max(0, Math.min(1, dragX / this.containerWidth));

      // Calculate target slide based on visible columns
      const maxScrollPosition = Math.max(0, this.slideCount - this.visibleColumns);
      const targetSlide = Math.min(Math.round(progress * maxScrollPosition), maxScrollPosition);

      this.slideshow.select(targetSlide);
    };

    const handleDragEnd = () => {
      this.isDragging = false;
      document.removeEventListener('pointermove', handleDrag);
      document.removeEventListener('pointerup', handleDragEnd);
    };

    document.addEventListener('pointermove', handleDrag);
    document.addEventListener('pointerup', handleDragEnd);
  }
}

if (!customElements.get('slideshow-scrollbar')) {
  customElements.define('slideshow-scrollbar', SlideshowScrollbar);
}
